/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useState, useEffect, useCallback } from 'react'

// api references
import { apiClient } from 'utils/api/apiClient'

// @mui core components

// Soft UI Dashboard PRO React components
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'

// Settings page components
import FormField from 'components/FormField'

// Data
import { selectData } from 'layouts/onboarding'
import { IntakeContext } from 'context/IntakeContext'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { format, differenceInYears, subYears } from 'date-fns'
import {
  Box,
  Button,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  Card,
  Grid,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import { callZapier } from 'utils/zapier/callZapier'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'
const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

const QuickSheetNew = () => {
  const {
    intakeState,
    updateIntakeStateAtKey,
    updateUser,
    currentErrors,
    isAdmin,
  } = useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your user profile', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  // const dateToYears = intakeState.dateOfBirth
  //   ? differenceInYears(new Date(), new Date(intakeState.dateOfBirth))
  //   : ''

  const [ageQuestion, setAgeQuestion] = useState(true)
  const [percentDollarsQuestion, setPercentDollarsQuestion] = useState(true)
  const [percentDollarsQuestionRoth, setPercentDollarsQuestionRoth] =
    useState(true)

  const dollarsToPercent = (contributionAmount) => {
    return intakeState.baseSalary
      ? ((contributionAmount * 26) / intakeState.baseSalary) * 100
      : 0
  }
  const percentToDollars = (contributionPercent) => {
    return intakeState.baseSalary
      ? (intakeState.baseSalary / 26) * (contributionPercent / 100)
      : 0
  }

  const dateToYears = (startDate, endDate) => {
    return startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  }
  const yearsToDate = (years) =>
    format(subYears(new Date(), years), 'yyyy-MM-dd')

  const userNameTitle =
    intakeState?.firstName && intakeState?.firstName
      ? intakeState?.firstName + ' ' + intakeState?.lastName
      : 'Missing Name'
  const showSurvivorBenefit = intakeState?.familyMaritalStatus === 'married'
  const showSurvivorBenefitOwed = intakeState?.familyDivorcedOwedPension
  const currentlyCollectingSS = intakeState?.alreadyCollectingSocialSecurity
  const showMilitary = intakeState?.military
  const showMilitaryReserves = intakeState?.militaryReserves
  const maritalStatus = intakeState?.familyMaritalStatus
  const divorcedStatus = intakeState?.familyDivorced
  const divorcedOwed = intakeState?.familyDivorcedOwedPension
  const showTSPLoans = intakeState?.tspLoans
  const showRothTSP = intakeState?.tspRoth
  const showTSPContribution = intakeState?.tsp
  const showFEGLI = intakeState?.fegli
  const lifeInsuranceOther = intakeState?.lifeInsuranceOther
  const healthInsurance = intakeState?.healthInsurance

  const sendToSalesforce = () => {
    const {
      createdAt,
      updatedAt,
      recommendations,
      documents,
      ...submittableValues
    } = intakeState
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/230xy7p/',
      payload: {
        user: intakeState,
      },
      sendNotification: false,
    })
    toast.success('You have successfully sent this client data to SalesForce', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  //get assets liabilities and deductions for creating google sheet
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundAssets = items?.filter(({ type }) => type === 'asset') || []
    setAssets(foundAssets)
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    setDeductions(foundDeductions)
  }, [])

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  const sendToGoogleSheets = () => {
    const {
      createdAt,
      updatedAt,
      recommendations,
      documents,
      ...submittableValues
    } = intakeState
    const tspAssets = [
      {
        name: 'TSP Traditional',
        subTypeClassification: '401(k)-tsptraditional',
        amount: intakeState?.tspBalance || 0,
      },
      {
        name: 'TSP Roth',
        subTypeClassification: '401(k)-tsproth',
        amount: intakeState?.tspRothBalance || 0,
      },
    ]
    const assetsWithTSP = [...tspAssets, ...assets]
    const quickSheetWithFinances = {
      ...intakeState,
      assets: assetsWithTSP,
      liabilities: liabilities,
      deductions: deductions,
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/22mzznj/',
      payload: {
        user: quickSheetWithFinances,
      },
      sendNotification: false,
    })
    toast.success(
      'You have successfully created a client spreadsheet in Google Sheets',
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
  }
  const sendToRetirementPaperwork = async () => {
    const dateOfBirthText = intakeState?.dateOfBirth
      ? format(new Date(intakeState.dateOfBirth), 'MM/dd/yyyy')
      : ''
    const dateOfPlannedRetirementText = intakeState?.dateOfPlannedRetirement
      ? format(new Date(intakeState.dateOfPlannedRetirement), 'MM/dd/yyyy')
      : ''
    const dateOfSpouseBirthText = intakeState?.familySpouseDateOfBirth
      ? format(new Date(intakeState.familySpouseDateOfBirth), 'MM/dd/yyyy')
      : ''
    const agencyLabel =
      selectData.agency.find(({ value }) => value === intakeState.agency)
        ?.label || ''
    const marriedText =
      intakeState?.familyMaritalStatus === 'married' ? 'X' : ''
    const unMarriedText =
      intakeState?.familyMaritalStatus !== 'married' ? 'X' : ''
    const armedForcesYes = intakeState?.military ? 'X' : ''
    const armedForcesNo = intakeState?.military ? '' : 'X'
    const retirementPaperwork = [
      'documentName=' +
        'Retirement-Paperwork-' +
        intakeState?.firstName +
        '-' +
        intakeState?.lastName +
        '.pdf',
      '1ANameLastFirstMiddle=' +
        intakeState?.lastName +
        ', ' +
        intakeState?.firstName,
      '1AAllOtherNames=',
      '1AAddressStreet=' + intakeState?.address,
      '1ACityStateZipcode=' +
        intakeState?.city +
        ', ' +
        intakeState?.state +
        ' ' +
        intakeState?.zipcode,
      '1ADaytimePhone=' + intakeState?.phone,
      '1ABestTime=',
      '1AEmail=' + intakeState?.email,
      '1ADOB=' + dateOfBirthText,
      '1ACitizen=X',
      '1BDateOfFinalSeparation=' + dateOfPlannedRetirementText,
      '1BAgency=' + agencyLabel,
      '1BTitleOfPosition=' + intakeState?.positionTitle,
      '1ADisability=X',
      '1CSpouseNameLastFirstMiddle=',
      '1CSpouseDOB=' + dateOfSpouseBirthText,
      '1BArmedForcesYes=' + armedForcesYes,
      '1BArmedForcesNo=' + armedForcesNo,
      '1BMilitaryRetiredPayNo=',
      '1BMilitaryRetiredPayYes=',
      '1CMarriedYes=' + marriedText,
      '1CMarriedNo=' + unMarriedText,
      '1CFormerSpouseAnnuityYes=',
      '1CFormerSpouseAnnuityNo=X',
      '2EFEHBYes=X',
      '2EFEHBNo=',
      '2ECourtYes=',
      '2ECourtNo=',
      '2EFEGLIYes=X',
      '2EFEGLINo=',
      '2EFDVYes=',
      '2EFDVNo=X',
      '2EFLTCYes=',
      '2EFLTCNo=X',
      '3HCheckingSavings=X',
      '3HDirectExpress=',
      '3HOutsideUS=',
      '3HTaxAnnuityYes=X',
      '3HTaxAnnuityNo=',
      '3HTaxSalaryYes=X',
      '3HTaxSalaryNo=',
      '4NameLastFirstMiddle=' +
        intakeState?.lastName +
        ', ' +
        intakeState?.firstName,
      '4DOB=' + dateOfBirthText,
      '4AMilitaryDepositYes=',
      '4AMilitaryDepositNo=X',
      '4CWorkersCompYes=X',
      '4COPMYes=X',
      '6EServiceComplete=X',
      '71NameLastFirstMiddle=' +
        intakeState?.lastName +
        ', ' +
        intakeState?.firstName,
      '71DOB=' + dateOfBirthText,
      '72NameFirstLast=' + intakeState?.firstName + ' ' + intakeState?.lastName,
      '8NameLastFirstMiddle=' +
        intakeState?.lastName +
        ', ' +
        intakeState?.firstName,
      '8DOB=' + dateOfBirthText,
      '8Agency=' + agencyLabel,
      '8BasicLifeYes=X',
      '8BasicLifeNo=',
      '8BasicLifeLB=',
      '8Reduction75=',
      '8OptionAYes=',
      '8OptionANo=',
      '8OptionANA=',
      '8OptionBYes=',
      '8OptionBNo=',
      '8OptionBNA=X',
      '8OptionBNoReductionMultiples=',
      '8OptionBFullReductionMultiples=',
      '8OptionCYes=',
      '8OptionCNo=',
      '8OptionCNA=X',
      '8OptionCNoReductionMultiples=',
      '8OptionCFullReductionMultiples=',
    ]
    const payload = {
      script: 'retirementPaperwork/pdfMapper.py',
      args: retirementPaperwork,
    }
    try {
      const response = await fetch(
        'https://evident-actually-tahr.ngrok-free.app/run-script',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      )
    } catch (e) {
      console.log(e)
    }
    // callZapier({
    //   user: intakeState,
    //   hookUrl: 'https://evident-actually-tahr.ngrok-free.app/run-script',
    //   payload: {
    //     script: 'retirementPaperwork/pdfMapper.py',
    //     args: retirementPaperwork,
    //   },
    //   sendNotification: false,
    // })
    toast.success('You have successfully created Retirement Paperwork', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container>
        <Grid item xs={12} xxl={10} sx={{ margin: 'auto' }}>
          <Box p={1}>
            <Card
              id="basic-info"
              sx={{
                overflow: 'visible',
                textAlign: 'left',
                boxShadow: 0,
                borderRadius: 2,
                border: '1px solid #D2D6DA',
                p: 2,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <Box px={3} pt={3}>
                    <Button
                      variant="contained"
                      sx={[
                        {
                          fontSize: '15px',
                          padding: '10px 24px 9px',
                          backgroundColor: '#449d48',
                          color: '#fff',
                          fontWeight: 'normal',
                          boxShadow: 'none',
                          margin: '0px 10px 10px 0px',
                        },
                        {
                          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                            transform: 'none',
                            backgroundColor: '#4CAF50',
                            boxShadow: 'none',
                          },
                        },
                      ]}
                      onClick={sendToGoogleSheets}
                    >
                      Create Client Google Sheet
                    </Button>
                    <Button
                      variant="contained"
                      sx={[
                        {
                          fontSize: '15px',
                          padding: '10px 24px 9px',
                          backgroundColor: '#449d48',
                          color: '#fff',
                          fontWeight: 'normal',
                          boxShadow: 'none',
                          margin: '0px 10px 10px 0px',
                        },
                        {
                          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                            transform: 'none',
                            backgroundColor: '#4CAF50',
                            boxShadow: 'none',
                          },
                        },
                      ]}
                      onClick={sendToSalesforce}
                    >
                      Create Lead in SalesForce
                    </Button>
                    <Button
                      variant="contained"
                      sx={[
                        {
                          fontSize: '15px',
                          padding: '10px 24px 9px',
                          backgroundColor: '#344767',
                          color: '#fff',
                          fontWeight: 'normal',
                          boxShadow: 'none',
                          margin: '0px 10px 10px 0px',
                        },
                        {
                          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                            transform: 'none',
                            backgroundColor: '#293852',
                            boxShadow: 'none',
                          },
                        },
                      ]}
                      onClick={onSubmit}
                    >
                      Update Client Info
                    </Button>
                    <Button
                      variant="contained"
                      sx={[
                        {
                          fontSize: '15px',
                          padding: '10px 24px 9px',
                          backgroundColor: '#344767',
                          color: '#fff',
                          fontWeight: 'normal',
                          boxShadow: 'none',
                          margin: '0px 10px 10px 0px',
                        },
                        {
                          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                            transform: 'none',
                            backgroundColor: '#293852',
                            boxShadow: 'none',
                          },
                        },
                      ]}
                      onClick={sendToRetirementPaperwork}
                    >
                      Create Retirement Paperwork
                    </Button>
                    <Button
                      variant="gradient"
                      color="dark"
                      sx={{
                        height: '100%',
                        padding: '8px 24px 7px',
                        margin: '0px 10px 10px 0px',
                      }}
                      onClick={onSubmit}
                      startIcon={
                        loading ? (
                          <>
                            <SavingIcon
                              fontSize="medium"
                              className={'loading'}
                            />
                          </>
                        ) : (
                          <>
                            <ChangesSavedIcon
                              fontSize="medium"
                              className={'saved'}
                            />
                          </>
                        )
                      }
                    >
                      {loading ? `updating...` : 'up to date'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Box py={3} px={3}>
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4">
                      {userNameTitle}'s Quick Reference
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'retirementStrategist',
                      label: 'Primary Strategist',
                      placeholder: 'Primary Strategist',
                      inputType: 'select',
                      value: intakeState.retirementStrategist,
                      onChange: updateIntakeStateAndSubmit(
                        (retirementStrategist) =>
                          updateIntakeStateAtKey({ retirementStrategist })
                      ),
                      options: selectData.retirementStrategist,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      id: 'salesforceAccountID',
                      label: 'Salesforce Account ID',
                      placeholder: 'Salesforce Account ID',
                      inputType: 'text',
                      value: intakeState.salesforceAccountID,
                      onChange: updateIntakeStateAndSubmit(
                        (salesforceAccountID) =>
                          updateIntakeStateAtKey({ salesforceAccountID })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      id: 'salesforceLeadID',
                      label: 'Salesforce Lead ID',
                      placeholder: 'Salesforce Lead ID',
                      inputType: 'text',
                      value: intakeState.salesforceLeadID,
                      onChange: updateIntakeStateAndSubmit((salesforceLeadID) =>
                        updateIntakeStateAtKey({ salesforceLeadID })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      id: 'salesforceICEventID',
                      label: 'Salesforce IC Event ID',
                      placeholder: 'Salesforce IC Event ID',
                      inputType: 'text',
                      value: intakeState.salesforceICEventID,
                      onChange: updateIntakeStateAndSubmit(
                        (salesforceICEventID) =>
                          updateIntakeStateAtKey({ salesforceICEventID })
                      ),
                    })}
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'accountStatus',
                      label: 'Account Status',
                      placeholder: 'Account Status',
                      inputType: 'select',
                      value: intakeState.accountStatus,
                      onChange: updateIntakeStateAndSubmit((accountStatus) =>
                        updateIntakeStateAtKey({ accountStatus })
                      ),
                      options: selectData.accountStatus,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      id: 'parcoScore',
                      label: 'PARCO Score',
                      placeholder: 'PARCO Score',
                      inputType: 'text',
                      value: intakeState.parcoScore,
                      onChange: updateIntakeStateAndSubmit((parcoScore) =>
                        updateIntakeStateAtKey({ parcoScore })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      id: 'retirementIncomeGap',
                      label: 'Income Gap',
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: intakeState.retirementIncomeGap,
                      onChange: updateIntakeStateAndSubmit(
                        (retirementIncomeGap) =>
                          updateIntakeStateAtKey({ retirementIncomeGap })
                      ),
                    })}
                  </Grid> */}
                </Grid>
              </Box>
              <Box component="form" pb={3} px={3}>
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Basic Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      id: 'firstName',
                      label: 'First Name',
                      placeholder: 'First Name',
                      inputType: 'text',
                      value: intakeState.firstName,
                      onChange: updateIntakeStateAndSubmit((firstName) =>
                        updateIntakeStateAtKey({
                          firstName,
                          fullName: `${firstName} ${intakeState?.lastName}`,
                        })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderMuiFormField({
                      id: 'lastName',
                      label: 'Last Name',
                      placeholder: 'Last Name',
                      inputType: 'text',
                      value: intakeState.lastName,
                      onChange: updateIntakeStateAndSubmit((lastName) =>
                        updateIntakeStateAtKey({
                          lastName,
                          fullName: `${intakeState?.firstName} ${lastName}`,
                        })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={8} sm={4} md={3}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'dateOfBirth',
                      label: 'Date of Birth',
                      placeholder: '01/01/1970',
                      inputType: 'date',
                      value: intakeState.dateOfBirth,
                      onChange: updateIntakeStateAndSubmit((dateOfBirth) =>
                        updateIntakeStateAtKey({ dateOfBirth })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={4} sm={2} xl={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      <FormField
                        disabled
                        type="text"
                        label="Age"
                        name="age"
                        placeholder="XX"
                        value={dateToYears(intakeState.dateOfBirth, new Date())}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      id: 'email',
                      label: 'Email',
                      placeholder: 'Email',
                      inputType: 'text',
                      value: intakeState.email,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      id: 'phone',
                      label: 'phone number',
                      placeholder: 'Eg. xxx-xxx-xxxx',
                      inputType: 'tel',
                      value: intakeState.phone,
                      onChange: updateIntakeStateAndSubmit((phone) =>
                        updateIntakeStateAtKey({ phone })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                    {renderMuiFormField({
                      id: 'secondaryEmail',
                      label: 'Work Email',
                      placeholder: 'Work Email',
                      inputType: 'text',
                      value: intakeState.secondaryEmail,
                      onChange: updateIntakeStateAndSubmit((secondaryEmail) =>
                        updateIntakeStateAtKey({ secondaryEmail })
                      ),
                    })}
                  </Grid>
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      id: 'address',
                      label: 'Street Address',
                      placeholder: '12345 Main Street',
                      inputType: 'text',
                      value: intakeState.address,
                      onChange: updateIntakeStateAndSubmit((address) =>
                        updateIntakeStateAtKey({ address })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      id: 'address2',
                      label: 'Apt, Suite, Bldg. (optional)',
                      placeholder: '',
                      inputType: 'text',
                      value: intakeState.address2,
                      onChange: updateIntakeStateAndSubmit((address2) =>
                        updateIntakeStateAtKey({ address2 })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      id: 'city',
                      label: 'City',
                      placeholder: 'City',
                      inputType: 'text',
                      value: intakeState.city,
                      onChange: updateIntakeStateAndSubmit((city) =>
                        updateIntakeStateAtKey({ city })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      id: 'state',
                      label: 'State',
                      placeholder: 'State',
                      inputType: 'text',
                      value: intakeState.state,
                      onChange: updateIntakeStateAndSubmit((state) =>
                        updateIntakeStateAtKey({ state })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      id: 'zipcode',
                      label: 'Zipcode',
                      placeholder: '#####',
                      inputType: 'zipcode',
                      value: intakeState.zipcode,
                      onChange: updateIntakeStateAndSubmit((zipcode) =>
                        updateIntakeStateAtKey({ zipcode })
                      ),
                    })}
                  </Grid>
                </Grid>
                {/* <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
              <Grid item xs={12} mt={4}>
                <Typography variant="h4">Family Details</Typography>
              </Grid>
            </Grid>
            <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
              <Grid item xs={12} mt={4}>
                <Typography variant="h4">Military Details</Typography>
              </Grid>
            </Grid> */}
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Employment Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'employmentStatus',
                      label: 'Employment Status',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.employmentStatus,
                      onChange: updateIntakeStateAndSubmit((employmentStatus) =>
                        updateIntakeStateAtKey({ employmentStatus })
                      ),
                      options: selectData.employmentStatus,
                    })}
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'agency',
                      label: 'Federal Agency',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.agency,
                      onChange: updateIntakeStateAndSubmit((agency) =>
                        updateIntakeStateAtKey({ agency })
                      ),
                      options: selectData.agency,
                    })}
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'positionTitle',
                      label: 'Position/Title',
                      placeholder: 'Position/Title',
                      inputType: 'text',
                      value: intakeState.positionTitle,
                      onChange: updateIntakeStateAndSubmit((positionTitle) =>
                        updateIntakeStateAtKey({ positionTitle })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'retirementSystem',
                      label: 'Retirement System',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.retirementSystem,
                      onChange: updateIntakeStateAndSubmit((retirementSystem) =>
                        updateIntakeStateAtKey({ retirementSystem })
                      ),
                      options: selectData.retirementSystem,
                    })}
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'dateOfHire',
                      label: 'Service Computation Date',
                      placeholder: '01/01/1970',
                      inputType: 'date',
                      value: intakeState.dateOfHire,
                      onChange: updateIntakeStateAndSubmit((dateOfHire) =>
                        updateIntakeStateAtKey({ dateOfHire })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={8} md={4} lg={3}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'dateOfPlannedRetirement',
                      label: 'Planned Retirement Date',
                      inputType: 'date',
                      value: intakeState.dateOfPlannedRetirement,
                      onChange: updateIntakeStateAndSubmit(
                        (dateOfPlannedRetirement) =>
                          updateIntakeStateAtKey({ dateOfPlannedRetirement })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={4} md={2} lg={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      <FormField
                        disabled
                        type="text"
                        label="Ret. Age"
                        name="age"
                        placeholder="XX"
                        value={dateToYears(
                          intakeState.dateOfBirth,
                          intakeState.dateOfPlannedRetirement
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Military Service</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'military',
                      label: 'Did you serve in the military?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.military,
                      onChange: updateIntakeStateAndSubmit((military) =>
                        updateIntakeStateAtKey({ military })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {showMilitary ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryBranch',
                          label: 'Military Branch',
                          placeholder: '',
                          inputType: 'select',
                          value: intakeState.militaryBranch,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryBranch) =>
                              updateIntakeStateAtKey({ militaryBranch })
                          ),
                          options: selectData.militaryBranch,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryActiveDutyPension',
                          label: 'Are you collecting an active duty pension?',
                          placeholder: '',
                          inputType: 'select',
                          intakeAttribute:
                            intakeState.militaryActiveDutyPension,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryActiveDutyPension) =>
                              updateIntakeStateAtKey({
                                militaryActiveDutyPension,
                              })
                          ),
                          options: selectData.yesNo,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                          <Grid item xs={12} sm={6}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'militaryActiveDutyStartDate',
                              label: 'Active Duty Start Date',
                              inputType: 'date',
                              intakeAttribute:
                                intakeState.militaryActiveDutyStartDate,
                              onChange: updateIntakeStateAndSubmit(
                                (militaryActiveDutyStartDate) =>
                                  updateIntakeStateAtKey({
                                    militaryActiveDutyStartDate,
                                  })
                              ),
                            })}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'militaryActiveDutyEndDate',
                              label: 'Active Duty Discharge Date',
                              inputType: 'date',
                              intakeAttribute:
                                intakeState.militaryActiveDutyEndDate,
                              onChange: updateIntakeStateAndSubmit(
                                (militaryActiveDutyEndDate) =>
                                  updateIntakeStateAtKey({
                                    militaryActiveDutyEndDate,
                                  })
                              ),
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryReserves',
                          label: 'Were you in the reserves?',
                          placeholder: '',
                          inputType: 'select',
                          value: intakeState.militaryReserves,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryReserves) =>
                              updateIntakeStateAtKey({ militaryReserves })
                          ),
                          options: selectData.yesNo,
                        })}
                      </Grid>
                      {showMilitaryReserves && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Grid
                              container
                              columnSpacing={3}
                              rowSpacing={2}
                              pt={1}
                            >
                              <Grid item xs={12} sm={6}>
                                {renderMuiFormField({
                                  autoFocus: false,
                                  id: 'militaryReservesStartDate',
                                  label: 'Reserves Start Date',
                                  inputType: 'date',
                                  intakeAttribute:
                                    intakeState.militaryReservesStartDate,
                                  onChange: updateIntakeStateAndSubmit(
                                    (militaryReservesStartDate) =>
                                      updateIntakeStateAtKey({
                                        militaryReservesStartDate,
                                      })
                                  ),
                                })}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                {renderMuiFormField({
                                  autoFocus: false,
                                  id: 'militaryReservesEndDate',
                                  label: 'Reserves Discharge Date',
                                  inputType: 'date',
                                  intakeAttribute:
                                    intakeState.militaryReservesEndDate,
                                  onChange: updateIntakeStateAndSubmit(
                                    (militaryReservesEndDate) =>
                                      updateIntakeStateAtKey({
                                        militaryReservesEndDate,
                                      })
                                  ),
                                })}
                              </Grid>
                            </Grid>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryPaidDeposit',
                          label:
                            'Have you paid a deposit to your agency for your active duty time?',
                          placeholder: '',
                          inputType: 'select',
                          value: intakeState.militaryPaidDeposit,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryPaidDeposit) =>
                              updateIntakeStateAtKey({
                                militaryPaidDeposit,
                              })
                          ),
                          options: selectData.yesNo,
                        })}
                      </Grid>
                    </>
                  ) : (
                    <></>
                  )}
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Family</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'familyMaritalStatus',
                      label: 'What is your current Marital Status?',
                      placeholder: intakeState.familyMaritalStatus,
                      inputType: 'select',
                      value: intakeState.familyMaritalStatus,
                      onChange: updateIntakeStateAndSubmit(
                        (familyMaritalStatus) =>
                          updateIntakeStateAtKey({
                            familyMaritalStatus: familyMaritalStatus,
                            familyDivorced:
                              familyMaritalStatus === 'divorced' ? true : false,
                          })
                      ),
                      options: selectData.relationshipStatus,
                    })}
                  </Grid>

                  {maritalStatus === 'married' && (
                    <>
                      <Grid item xs={0} sm={6}></Grid>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          id: 'familySpouseName',
                          label: "Spouse's Name",
                          placeholder: "Spouse's Name",
                          inputType: 'text',
                          //   defaultErrorState: currentErrors[intakeState.firstName],
                          value: intakeState.familySpouseName,
                          onChange: updateIntakeStateAndSubmit(
                            (familySpouseName) =>
                              updateIntakeStateAtKey({
                                familySpouseName,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          id: 'familySpousePhone',
                          label: "Spouse's Phone #",
                          placeholder: "Spouse's #",
                          inputType: 'tel',
                          //   defaultErrorState: currentErrors[intakeState.firstName],
                          value: intakeState.familySpousePhone,
                          onChange: updateIntakeStateAndSubmit(
                            (familySpousePhone) =>
                              updateIntakeStateAtKey({
                                familySpousePhone,
                              })
                          ),
                        })}
                      </Grid>

                      {ageQuestion ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Grid item xs={12}>
                              {renderMuiFormField({
                                id: 'familySpouseDateOfBirth',
                                label: 'Spouse Date of Birth',
                                inputType: 'date',
                                defaultDate: '1970-01-01',
                                dateListTrim: { start: 0, end: 80 },
                                intakeAttribute:
                                  intakeState.familySpouseDateOfBirth,
                                onChange: updateIntakeStateAndSubmit(
                                  (familySpouseDateOfBirth) =>
                                    updateIntakeStateAtKey({
                                      familySpouseDateOfBirth,
                                    })
                                ),
                              })}
                            </Grid>

                            <Box display="flex" justifyContent="flex-start">
                              <Button
                                variant="text"
                                color="info"
                                sx={{ height: '100%' }}
                                onClick={() => setAgeQuestion(false)}
                              >
                                Use their age instead
                              </Button>
                            </Box>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Grid item xs={12}>
                              <FormField
                                label={<>Spouse&apos;s Age</>}
                                placeholder="e.g. 62"
                                value={dateToYears(
                                  intakeState.familySpouseDateOfBirth,
                                  new Date()
                                )}
                                onChange={(e) => {
                                  updateIntakeStateAtKey({
                                    familySpouseDateOfBirth: yearsToDate(
                                      e.target.value
                                    ),
                                  })
                                }}
                              />
                            </Grid>

                            <Box display="flex" justifyContent="flex-start">
                              <Button
                                variant="text"
                                color="info"
                                sx={{ height: '100%' }}
                                onClick={() => setAgeQuestion(true)}
                              >
                                Use their date of birth instead
                              </Button>
                            </Box>
                          </Grid>
                        </>
                      )}
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'familyDivorced',
                      label: 'Have you ever been divorced?',
                      placeholder: intakeState.familyDivorced,
                      inputType: 'select',
                      value: intakeState.familyDivorced,
                      onChange: updateIntakeStateAndSubmit((familyDivorced) =>
                        updateIntakeStateAtKey({ familyDivorced })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>

                  {divorcedStatus && (
                    <>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'familyDivorcedOwedPension',
                          label:
                            'Will your former spouse be owed any of your pension?',
                          placeholder: intakeState.familyDivorcedOwedPension,
                          inputType: 'select',
                          intakeAttribute:
                            intakeState.familyDivorcedOwedPension,
                          onChange: updateIntakeStateAndSubmit(
                            (familyDivorcedOwedPension) =>
                              updateIntakeStateAtKey({
                                familyDivorcedOwedPension,
                              })
                          ),
                          options: selectData.yesNo,
                        })}
                      </Grid>

                      {divorcedOwed && (
                        <Grid item xs={12} sm={6}>
                          <Grid
                            container
                            columnSpacing={3}
                            rowSpacing={2}
                            pt={1}
                          >
                            <Grid item xs={12} sm={6}>
                              {renderMuiFormField({
                                id: 'familyDivorcedMarriageStartDate',
                                label: 'Marriage date with former spouse',
                                inputType: 'date',
                                defaultDate: '1970-01-01',
                                dateListTrim: { start: 0, end: 80 },
                                intakeAttribute:
                                  intakeState.familyDivorcedMarriageStartDate,
                                onChange: updateIntakeStateAndSubmit(
                                  (familyDivorcedMarriageStartDate) =>
                                    updateIntakeStateAtKey({
                                      familyDivorcedMarriageStartDate,
                                    })
                                ),
                              })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {renderMuiFormField({
                                id: 'familyDivorcedMarriageEndDate',
                                label: 'Divorce date with former spouse',
                                inputType: 'date',
                                defaultDate: '1970-01-01',
                                dateListTrim: { start: 0, end: 80 },
                                intakeAttribute:
                                  intakeState.familyDivorcedMarriageEndDate,
                                onChange: updateIntakeStateAndSubmit(
                                  (familyDivorcedMarriageEndDate) =>
                                    updateIntakeStateAtKey({
                                      familyDivorcedMarriageEndDate,
                                    })
                                ),
                              })}
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'familyChildren',
                      label: 'Number of children (if any)',
                      placeholder: intakeState.familyChildren,
                      inputType: 'int',
                      value: intakeState.familyChildren,
                      onChange: updateIntakeStateAndSubmit((familyChildren) =>
                        updateIntakeStateAtKey({ familyChildren })
                      ),
                    })}
                  </Grid>
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Pay Stub Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'baseSalary',
                      label: (
                        <>
                          <span>
                            Adjusted Base Annual Salary
                            <span style={{ color: 'red' }}> *</span>
                            <Tooltip
                              TransitionComponent={Fade}
                              title="Your Adjusted base salary is your base salary including any locality pay you receive."
                              placement="top"
                              color="info"
                            >
                              <IconButton size="small" color="dark">
                                <HelpIcon sx={{ marginTop: '-6px' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </>
                      ),
                      placeholder: '$62,000.00',
                      inputType: 'currencyFloat',
                      value: intakeState.baseSalary || 0,
                      onChange: updateIntakeStateAndSubmit((baseSalary) =>
                        updateIntakeStateAtKey({ baseSalary })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'highThree',
                      label: (
                        <>
                          <span>
                            High Three
                            <span style={{ color: 'red' }}> *</span>
                            <Tooltip
                              TransitionComponent={Fade}
                              title="Highest three consecutive years of service. Used to calculate your pension"
                              placement="top"
                              color="info"
                            >
                              <IconButton size="small" color="dark">
                                <HelpIcon sx={{ marginTop: '-6px' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </>
                      ),
                      placeholder: '$62,000.00',
                      inputType: 'currencyFloat',
                      value: intakeState.highThree,
                      onChange: updateIntakeStateAndSubmit((highThree) =>
                        updateIntakeStateAtKey({ highThree })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      id: 'netTakeHome',
                      label: (
                        <>
                          <span>
                            Net take home per pay period
                            <span style={{ color: 'red' }}> *</span>
                            <Tooltip
                              TransitionComponent={Fade}
                              title="How much you take home in a normal pay period net of all deductions"
                              placement="top"
                              color="info"
                            >
                              <IconButton size="small" color="dark">
                                <HelpIcon sx={{ marginTop: '-6px' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </>
                      ),
                      placeholder: '$3,500',
                      inputType: 'currencyFloat',
                      value: intakeState.netTakeHome,
                      onChange: updateIntakeStateAndSubmit((netTakeHome) =>
                        updateIntakeStateAtKey({ netTakeHome })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'currentLeaveBalance',
                      label: 'Current Earned Annual Leave Balance',
                      placeholder: '100',
                      inputType: 'int',
                      value: intakeState.currentLeaveBalance,
                      onChange: updateIntakeStateAndSubmit(
                        (currentLeaveBalance) =>
                          updateIntakeStateAtKey({ currentLeaveBalance })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'currentSickLeaveBalance',
                      label: 'Current Sick Leave Balance',
                      placeholder: '100',
                      inputType: 'int',
                      value: intakeState.currentSickLeaveBalance,
                      onChange: updateIntakeStateAndSubmit(
                        (currentSickLeaveBalance) =>
                          updateIntakeStateAtKey({ currentSickLeaveBalance })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'taxRate',
                      label: 'Estimated All-In Tax Rate',
                      placeholder: 'Ex. 15.0%',
                      inputType: 'percentage',
                      value: intakeState.taxRate,
                      onChange: updateIntakeStateAndSubmit((taxRate) =>
                        updateIntakeStateAtKey({ taxRate })
                      ),
                    })}
                  </Grid>
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Deductions</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      inputType: 'deductionsReview',
                    })}
                  </Grid>
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">TSP Details</Typography>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="h5">
                      Traditional TSP Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tsp',
                      label: 'Do you contribute to the Traditional TSP?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.tsp,
                      onChange: updateIntakeStateAndSubmit((tsp) =>
                        updateIntakeStateAtKey({ tsp })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspBalance',
                      label: 'Current TSP balance',
                      placeholder: '$250,000.00',
                      inputType: 'currencyFloat',
                      value: intakeState.tspBalance,
                      onChange: updateIntakeStateAndSubmit((tspBalance) =>
                        updateIntakeStateAtKey({ tspBalance })
                      ),
                    })}
                  </Grid>
                  {showTSPContribution && (
                    <>
                      {percentDollarsQuestion ? (
                        <Grid item xs={12} sm={6}>
                          <Grid item xs={12}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'tspContribution',
                              label: 'What percentage do you contribute?',
                              placeholder: 'Ex. 5.0%',
                              inputType: 'percentage',
                              value: intakeState.tspContribution,
                              onChange: (tspContribution) => {
                                updateIntakeStateAtKey({
                                  tspContribution,
                                  tspContributionAmount:
                                    percentToDollars(tspContribution),
                                })
                              },
                            })}
                          </Grid>
                          <Box display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              color="info"
                              sx={{ height: '100%' }}
                              onClick={() => setPercentDollarsQuestion(false)}
                            >
                              Use amount contributed per pay period instead
                            </Button>
                          </Box>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={6}>
                          <Grid item xs={12}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'tspContributionAmount',
                              label:
                                'What amount do you contribute per pay period?',
                              placeholder: '$135.00',
                              inputType: 'currencyFloat',
                              intakeAttribute:
                                intakeState.tspContributionAmount,
                              onChange: (tspContributionAmount) => {
                                updateIntakeStateAtKey({
                                  tspContributionAmount,
                                  tspContribution: dollarsToPercent(
                                    tspContributionAmount
                                  ),
                                })
                              },
                            })}
                          </Grid>
                          <Box display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              color="info"
                              sx={{ height: '100%' }}
                              onClick={() => setPercentDollarsQuestion(true)}
                            >
                              Use percentage contributed per pay period instead
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* ROTH TSP */}
                  <Grid item xs={12} mt={2}>
                    <Typography variant="h5">Roth TSP Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspRoth',
                      label: 'Do you contribute to the Roth TSP?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.tspRoth,
                      onChange: updateIntakeStateAndSubmit((tspRoth) =>
                        updateIntakeStateAtKey({ tspRoth })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspRothBalance',
                      label: 'Current Roth TSP balance',
                      placeholder: '$250,000.00',
                      inputType: 'currencyFloat',
                      value: intakeState.tspRothBalance,
                      onChange: updateIntakeStateAndSubmit((tspRothBalance) =>
                        updateIntakeStateAtKey({ tspRothBalance })
                      ),
                    })}
                  </Grid>

                  {showRothTSP && (
                    <>
                      {percentDollarsQuestionRoth ? (
                        <Grid item xs={12} sm={6}>
                          <Grid item xs={12}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'tspRothContribution',
                              label: 'What percentage do you contribute?',
                              placeholder: 'Ex. 5.0%',
                              inputType: 'percentage',
                              value: intakeState.tspRothContribution,
                              onChange: (tspRothContribution) => {
                                updateIntakeStateAtKey({
                                  tspRothContribution,
                                  tspRothContributionAmount:
                                    percentToDollars(tspRothContribution),
                                })
                              },
                            })}
                          </Grid>
                          <Box display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              color="info"
                              sx={{ height: '100%' }}
                              onClick={() =>
                                setPercentDollarsQuestionRoth(false)
                              }
                            >
                              Use amount contributed per pay period instead
                            </Button>
                          </Box>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={6}>
                          <Grid item xs={12}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'tspRothContributionAmount',
                              label:
                                'What amount do you contribute per pay period?',
                              placeholder: '$135.00',
                              inputType: 'currencyFloat',
                              intakeAttribute:
                                intakeState.tspRothContributionAmount,
                              onChange: (tspRothContributionAmount) => {
                                updateIntakeStateAtKey({
                                  tspRothContributionAmount,
                                  tspRothContribution: dollarsToPercent(
                                    tspRothContributionAmount
                                  ),
                                })
                              },
                            })}
                          </Grid>
                          <Box display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              color="info"
                              sx={{ height: '100%' }}
                              onClick={() =>
                                setPercentDollarsQuestionRoth(true)
                              }
                            >
                              Use percentage contributed per pay period instead
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}
                  <Grid item xs={12} mt={2}>
                    <Typography variant="h5">TSP Loan Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspLoans',
                      label: 'Do you have any TSP loans?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.tspLoans,
                      onChange: updateIntakeStateAndSubmit((tspLoans) =>
                        updateIntakeStateAtKey({ tspLoans })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>

                  {showTSPLoans && (
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        inputType: 'tspLoanReview',
                      })}
                    </Grid>
                  )}
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">
                      Social Security / Pension
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'alreadyCollectingSocialSecurity',
                      label: 'Are you currently collecting Social Security?',
                      placeholder: '',
                      inputType: 'select',
                      intakeAttribute:
                        intakeState.alreadyCollectingSocialSecurity,
                      onChange: updateIntakeStateAndSubmit(
                        (alreadyCollectingSocialSecurity) =>
                          updateIntakeStateAtKey({
                            alreadyCollectingSocialSecurity,
                          })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {currentlyCollectingSS ? (
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'estimatedSocialSecurity',
                        label: 'What is your Monthly Social Security benefit?',
                        placeholder: '$2,400.00',
                        inputType: 'currencyFloat',
                        value: intakeState.estimatedSocialSecurity,
                        onChange: updateIntakeStateAndSubmit(
                          (estimatedSocialSecurity) =>
                            updateIntakeStateAtKey({ estimatedSocialSecurity })
                        ),
                      })}
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'estimatedSocialSecurity',
                        label:
                          'Estimated Social Security benefit at full retirement age',
                        placeholder: '$2,400.00',
                        inputType: 'currencyFloat',
                        value: intakeState.estimatedSocialSecurity,
                        onChange: updateIntakeStateAndSubmit(
                          (estimatedSocialSecurity) =>
                            updateIntakeStateAtKey({ estimatedSocialSecurity })
                        ),
                      })}
                    </Grid>
                  )}
                  {showSurvivorBenefit ? (
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'survivorBenefitElection',
                        label: 'Survivor Benefit Election',
                        placeholder: '',
                        inputType: 'select',
                        value: intakeState.survivorBenefitElection,
                        onChange: updateIntakeStateAndSubmit(
                          (survivorBenefitElection) =>
                            updateIntakeStateAtKey({ survivorBenefitElection })
                        ),
                        options: selectData.survivorBenefitElection,
                      })}
                    </Grid>
                  ) : (
                    <></>
                  )}
                  {showSurvivorBenefitOwed ? (
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'familyDivorcedOwedPension',
                        label: 'Survivor Benefit Owed to Former Spouse',
                        placeholder: '',
                        inputType: 'select',
                        value: intakeState.familyDivorcedOwedPension,
                        onChange: updateIntakeStateAndSubmit(
                          (familyDivorcedOwedPension) =>
                            updateIntakeStateAtKey({
                              familyDivorcedOwedPension,
                            })
                        ),
                        options: selectData.survivorBenefitElection,
                      })}
                    </Grid>
                  ) : (
                    <></>
                  )}
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">FEGLI / Life Insurance</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'fegli',
                      label: 'Are you currently enrolled in FEGLI coverage?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.fegli,
                      onChange: updateIntakeStateAndSubmit((fegli) =>
                        updateIntakeStateAtKey({ fegli })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {showFEGLI && (
                    <>
                      <Grid item xs={12} sm={12}>
                        <Typography
                          component="label"
                          variant="body2"
                          fontWeight="regular"
                          textTransform="capitalize"
                        >
                          Do you have any optional FEGLI coverage?
                        </Typography>

                        {/* this currently does not debounce properly, but does update the intakeState */}
                        <OnboardingMultiSelectTile
                          header={false}
                          helpText={'test'}
                          subHeader={false}
                          tileSize={{ xs: '4', sm: '4', md: '4', lg: '4' }}
                          questionArray={[
                            {
                              id: 'fegliOptionA',
                              optionLabel: 'Option A',
                            },
                            {
                              id: 'fegliOptionB',
                              optionLabel: 'Option B',
                            },
                            {
                              id: 'fegliOptionC',
                              optionLabel: 'Option C',
                            },
                          ]}
                        />
                      </Grid>

                      {intakeState.fegliOptionB && (
                        <Grid
                          item
                          xs={12}
                          sm={intakeState.fegliOptionC ? 6 : 12}
                        >
                          {renderMuiFormField({
                            autoFocus: false,
                            id: 'fegliOptionBMultiples',
                            label:
                              'How many multiples of option B do you have?',
                            placeholder: '0',
                            inputType: 'int',
                            value: intakeState.fegliOptionBMultiples,
                            onChange: updateIntakeStateAndSubmit(
                              (fegliOptionBMultiples) =>
                                updateIntakeStateAtKey({
                                  fegliOptionBMultiples,
                                })
                            ),
                          })}
                        </Grid>
                      )}
                      {intakeState.fegliOptionC && (
                        <Grid
                          item
                          xs={12}
                          sm={intakeState.fegliOptionB ? 6 : 12}
                        >
                          {renderMuiFormField({
                            autoFocus: false,
                            id: 'fegliOptionCMultiples',
                            label:
                              'How many multiples of option C do you have?',
                            placeholder: '0',
                            inputType: 'int',
                            value: intakeState.fegliOptionCMultiples,
                            onChange: updateIntakeStateAndSubmit(
                              (fegliOptionCMultiples) =>
                                updateIntakeStateAtKey({
                                  fegliOptionCMultiples,
                                })
                            ),
                          })}
                        </Grid>
                      )}
                    </>
                  )}
                  <Grid item xs={12} mt={2}>
                    <Typography variant="h5">Private Life Insurance</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'lifeInsuranceOther',
                      label: 'Do you have any private life insurance?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.lifeInsuranceOther,
                      onChange: updateIntakeStateAndSubmit(
                        (lifeInsuranceOther) =>
                          updateIntakeStateAtKey({ lifeInsuranceOther })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {lifeInsuranceOther && (
                    <>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'lifeInsuranceOtherProvider',
                          label: 'Who is your provider?',
                          placeholder: '',
                          inputType: 'select',
                          intakeAttribute:
                            intakeState.lifeInsuranceOtherProvider,
                          onChange: updateIntakeStateAndSubmit(
                            (lifeInsuranceOtherProvider) =>
                              updateIntakeStateAtKey({
                                lifeInsuranceOtherProvider,
                              })
                          ),
                          options: selectData.privateLifeInsuranceProviders,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'lifeInsuranceOtherCoverageAmount',
                          label: 'Coverage Amount',
                          placeholder: '$250,000.00',
                          inputType: 'currencyFloat',
                          intakeAttribute:
                            intakeState.lifeInsuranceOtherCoverageAmount,
                          onChange: updateIntakeStateAndSubmit(
                            (lifeInsuranceOtherCoverageAmount) =>
                              updateIntakeStateAtKey({
                                lifeInsuranceOtherCoverageAmount,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'lifeInsuranceOtherPremium',
                          label: 'Monthly Premium',
                          placeholder: '$250.00',
                          inputType: 'currencyFloat',
                          intakeAttribute:
                            intakeState.lifeInsuranceOtherPremium,
                          onChange: updateIntakeStateAndSubmit(
                            (lifeInsuranceOtherPremium) =>
                              updateIntakeStateAtKey({
                                lifeInsuranceOtherPremium,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'lifeInsuranceOtherType',
                          label: 'Coverage Type',
                          placeholder: 'Type',
                          inputType: 'select',
                          value: intakeState.lifeInsuranceOtherType,
                          onChange: updateIntakeStateAndSubmit(
                            (lifeInsuranceOtherType) =>
                              updateIntakeStateAtKey({
                                lifeInsuranceOtherType,
                              })
                          ),
                          options: selectData.privateLifeInsuranceTypes,
                        })}
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
                  <Grid item xs={12} mt={4}>
                    <Typography variant="h4">Healthcare</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'healthInsurance',
                      label: 'Health insurance program',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.healthInsurance,
                      onChange: updateIntakeStateAndSubmit((healthInsurance) =>
                        updateIntakeStateAtKey({ healthInsurance })
                      ),
                      options: selectData.healthInsurance,
                    })}
                  </Grid>

                  {healthInsurance === 'fehb' && (
                    <Grid item xs={12} md={8}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'fehbCode',
                        label: 'Select Your FEHB Code',
                        placeholder: '',
                        inputType: 'select',
                        value: intakeState.fehbCode,
                        onChange: updateIntakeStateAndSubmit((fehbCode) =>
                          updateIntakeStateAtKey({ fehbCode })
                        ),
                        options: selectData.fehbCode,
                      })}
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'healthInsurancePremium',
                      label: 'What is your healthcare premium per pay period?',
                      placeholder: '$275.00',
                      inputType: 'currencyFloat',
                      value: intakeState.healthInsurancePremium,
                      onChange: updateIntakeStateAndSubmit(
                        (healthInsurancePremium) =>
                          updateIntakeStateAtKey({ healthInsurancePremium })
                      ),
                    })}
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </DashboardLayout>
  )
}

export default QuickSheetNew
